import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { Store } from '@ngrx/store';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { filter, Observable, Subject, takeUntil } from 'rxjs';

import { ITrialState } from './core/store/user/models/trial';
import { IUser } from './core/store/user/models/UserMe';
import * as actions from './core/store/user/user.actions';
import * as selectors from './core/store/user/user.selectors';
import { LoaderComponent } from './modules/shared/components/loader/loader.component';
import { SidebarComponent } from './modules/shared/components/sidebar/sidebar.component';
import { SvgIconComponent } from './modules/shared/components/svg-icon/svg-icon.component';
import { ToastMessageComponent } from './modules/shared/components/toast-message/toast-message.component';
import { TrialBannerComponent } from './modules/shared/components/trial-banner/trial-banner.component';
import { CustomTooltipDirective } from './modules/shared/directives/custom-tooltip.directive';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    CommonModule,
    ToastMessageComponent,
    SvgIconComponent,
    SidebarComponent,
    RouterOutlet,
    NzLayoutModule,
    LoaderComponent,
    CustomTooltipDirective,
    TrialBannerComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  isCollapsed: boolean | null = null;
  isAuthenticated$: Observable<boolean>;
  title: 'intell-ui';
  userLoggedIn = false;

  /**
   * Suppresses the sidebar and trial banner on routes that must stand alone. The Auth0
   * session is still valid on those routes — only the tenant is gone — so the shell's
   * authentication check does not exclude them, and the navigation would otherwise
   * render around a screen saying the account no longer exists.
   */
  shellHidden = false;

  private static readonly ShellLessRoutes = ['/account-unavailable'];

  private destroy$ = new Subject<void>();

  constructor(
    private readonly auth: AuthService,
    private readonly store: Store,
    private readonly router: Router,
  ) {
    this.shellHidden = AppComponent.isShellLess(this.router.url);
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event) => {
        this.shellHidden = AppComponent.isShellLess(event.urlAfterRedirects);
      });
  }

  private static isShellLess(url: string): boolean {
    return AppComponent.ShellLessRoutes.some((route) => url.startsWith(route));
  }

  user$: Observable<IUser> = this.store.select(selectors.getUserMe);
  trial$: Observable<ITrialState | null> = this.store.select(
    selectors.getTrialState,
  );

  toggleSideNabar(): void {
    this.isCollapsed = !this.isCollapsed;
    this.store.dispatch(
      actions.setUserPreferences({ navigationToggled: this.isCollapsed }),
    );
  }

  ngOnInit(): void {
    this.isAuthenticated$ = this.auth.isAuthenticated$;

    // Subscribe to userPreferences$ (boolean)
    this.user$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      // Set collapsed state directly from boolean
      this.isCollapsed = user?.navigationToggled ?? false;

      // Mark preferences as loaded so layout can render
      this.userLoggedIn = true;
    });
  }

  auth0login(): void {
    this.auth.loginWithRedirect();
  }

  auth0logout(): void {
    this.auth.logout();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
